import { Flex, IconButton, Text, TextField } from '@radix-ui/themes'
import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { useState } from 'react'

type MaskedValueProps = {
  value?: string | null
  editable?: boolean
  onChange?: (value: string) => void
  /** Async source for the revealed value (e.g. a permission-gated decrypt fetch). When set, the
   *  first reveal calls this and caches the result; when absent, `value` is revealed directly. */
  onReveal?: () => Promise<string>
  /** What to show while hidden. Defaults to a dot per character of `value` (SSN last-4). */
  maskedDisplay?: string
  /** false hides the toggle entirely — the permission gate (masked display, no reveal control). */
  canReveal?: boolean
  /** Accessible noun for the toggle: `Show ${label}` / `Hide ${label}`. */
  label?: string
  /** Editable mode only — forwarded to the input. */
  placeholder?: string
  /** Editable mode only — forwarded to the input. */
  maxLength?: number
}

/** Hidden-by-default sensitive value with an inline reveal toggle. Revealed value is either
 *  in-hand (`value`) or fetched on demand (`onReveal`). */
export const MaskedValue = ({
  value,
  editable,
  onChange,
  onReveal,
  maskedDisplay,
  canReveal = true,
  label = 'value',
  placeholder,
  maxLength,
}: MaskedValueProps) => {
  const [visible, setVisible] = useState(false)
  const [fetched, setFetched] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const masked = maskedDisplay ?? '•'.repeat((value ?? '').length)

  // Read-only with nothing to show: match DetailField's N/A fallback, no toggle.
  if (!editable && !value && !maskedDisplay) {
    return <Text>N/A</Text>
  }

  const handleToggle = async () => {
    // First reveal with an async source: fetch once, cache, then show.
    if (onReveal && fetched === null) {
      setLoading(true)
      setFailed(false)
      try {
        setFetched(await onReveal())
        setVisible(true)
      } catch {
        setFailed(true)
      } finally {
        setLoading(false)
      }
      return
    }
    setVisible((prev) => !prev)
  }

  const toggle = canReveal ? (
    <IconButton
      type="button"
      size="1"
      variant="ghost"
      color="gray"
      loading={loading}
      onClick={handleToggle}
      aria-label={visible ? `Hide ${label}` : `Show ${label}`}>
      {visible ? <EyeClosedIcon /> : <EyeOpenIcon />}
    </IconButton>
  ) : null

  if (editable) {
    return (
      <Flex align="center" gap="2" width="100%">
        <TextField.Root
          type={visible ? 'text' : 'password'}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          // Fill the cell so a masked input lines up with the unmasked ones beside it.
          style={{ flex: 1 }}
        />
        {toggle}
      </Flex>
    )
  }

  return (
    <Flex align="center" gap="2">
      <Text>{visible ? (onReveal ? fetched : value) : masked}</Text>
      {toggle}
      {failed ? (
        <Text size="1" color="red">
          Could not load {label}
        </Text>
      ) : null}
    </Flex>
  )
}
